import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  FormLabel,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { formatDateTime } from '../../../../helpers'
import { ICourseInterface } from '../../../../interfaces/Website/Courses/CoursesCategory/ICoursesCategory'
import { CoursesCategoryService } from '../../../../services/CoursesCategory/CoursesCategoryService'
import { CoursesCategoryModal } from './CoursesCategoryModal'

const CoursesCategoryPage = () => {
  const service = new CoursesCategoryService()
  const cancelRef = useRef(null)
  const toast = useToast()

  const [categories, setCategories] = useState<ICourseInterface[]>([])
  const [filtered, setFiltered] = useState<ICourseInterface[]>([])
  const [error, setError] = useState(false)

  const [search, setSearch] = useState('')

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idDeleting, setIdDeleting] = useState<number | null>(null)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ICourseInterface | null>(null)

  const fetchCategories = async () => {
    try {
      const data = await service.list()
      setCategories(data)
      setFiltered(data)
      setError(false)
    } catch (error) {
      setError(true)
    }
  }

  const closeDeleteDialog = () => {
    setIsDeleteOpen(false)
    setIdDeleting(null)
  }

  const handleDelete = async () => {
    if (!idDeleting) return

    try {
      await service.delete(idDeleting)
      toast({ title: 'Categoria excluída com sucesso', status: 'success' })
      fetchCategories()
    } catch (error) {
      toast({ title: 'Erro ao excluir categoria', status: 'error' })
    } finally {
      setIsDeleteOpen(false)
      setIdDeleting(null)
    }
  }

  const openCreateModal = () => {
    setEditingCategory(null)
    setIsModalOpen(true)
  }

  const openEditModal = (category: ICourseInterface) => {
    setEditingCategory(category)
    setIsModalOpen(true)
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    const term = search.toLowerCase()
    const filteredData = categories.filter((item) => item.name.toLowerCase().includes(term))
    setFiltered(filteredData)
  }, [search, categories])

  return (
    <Box>
      <Flex flex="1" justifyContent="flex-end" alignItems="center" mb={4}>
        <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
          Pesquisar
        </FormLabel>
        <Input
          type="search"
          size="sm"
          variant="outline"
          id="search"
          w="200px"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>
      {error ? (
        <Center py={10}>
          <Text color="red.500">Erro ao carregar categorias.</Text>
        </Center>
      ) : (
        <TableContainer>
          <Table colorScheme="gray">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Nome</Th>
                <Th>Criado em</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.length === 0 ? (
                <Tr>
                  <Td colSpan={4} textAlign="center">
                    Nenhum resultado encontrado
                  </Td>
                </Tr>
              ) : (
                filtered.map((category) => (
                  <Tr key={category.id}>
                    <Td>{category.id}</Td>
                    <Td>{category.name}</Td>
                    <Td>{formatDateTime(category.created_at)}</Td>
                    <Td>
                      <Tooltip label="Editar">
                        <IconButton
                          aria-label="Editar"
                          icon={<i className="lni lni-pencil" />}
                          colorScheme="blue"
                          size="sm"
                          mr={2}
                          onClick={() => openEditModal(category)}
                        />
                      </Tooltip>
                      <Tooltip label="Excluir">
                        <IconButton
                          aria-label="Excluir"
                          icon={<i className="lni lni-trash-can" />}
                          colorScheme="red"
                          size="sm"
                          onClick={() => {
                            setIsDeleteOpen(true)
                            setIdDeleting(category.id)
                          }}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <Button
        colorScheme="magenta"
        position="fixed"
        bottom="20px"
        right="20px"
        borderRadius="full"
        width="70px"
        height="70px"
        onClick={openCreateModal}
      >
        <i className="lni lni-plus" />
      </Button>

      <CoursesCategoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onFinished={fetchCategories}
        initialData={editingCategory}
        service={service}
      />

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={closeDeleteDialog} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar exclusão
            </AlertDialogHeader>

            <AlertDialogBody>Tem certeza que deseja excluir essa categoria?</AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeDeleteDialog}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default CoursesCategoryPage
